import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useExcQuotemediaV1EodPriceTaskQueryServiceEodPriceTaskGet } from '@interxfi/api-client/generated/hooks/exc-quotemedia-v1-eod-price-task-query-service-eod-price-task-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ExcQuotemediaV1EodPriceTaskState } from '@interxfi/types/generated/api';
import {
  QuoteMediaEodPriceTaskEventDrawer,
  QuoteMediaEodPriceTaskEventSummary,
} from '../../../../../../../../../../features/vendor/quotemedia/eod-price/task/quotemedia-eod-price-task-event';
import { getQuoteMediaEodPriceTaskStateListItems } from '../../../../../../../../../../features/vendor/quotemedia/eod-price/task/quotemedia-eod-price-task-list-items';
import { getQuotemediaEodPriceTaskDetailPath } from '../../../../../../../../../../features/vendor/quotemedia/eod-price/task/quotemedia-eod-price-task-routing';
import { QuotemediaEodPriceTaskStatusPill } from '../../../../../../../../../../features/vendor/quotemedia/eod-price/task/quotemedia-eod-price-task-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/quote-media/task/_task/$taskId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `quotemedia-eod-price-task-${params.taskId}`,
        label: 'Task',
        to: getQuotemediaEodPriceTaskDetailPath(params.taskId),
        suffix: <CopyButton content={params.taskId} label="Copy Task ID" tooltip={{ copy: params.taskId, copied: 'Copied Task ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('QuoteMedia EOD Price Task'),
  component: QuoteMediaEodPriceTaskDetail,
});

function getListItems(data: ExcQuotemediaV1EodPriceTaskState | undefined) {
  const items = getQuoteMediaEodPriceTaskStateListItems(data);

  return {
    general: {
      left: [items.data.spec.date, items.data.spec.symbol, items.data.spec.assetId],
      right: [items.metadata.createdAt, items.metadata.updatedAt, items.taskId, items.jobId],
    },
  };
}

function getMenuItems(taskId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: QuoteMediaEodPriceTaskEventDrawer,
        props: { request: { taskId } },
      },
    },
    actions: [],
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load end of day price task', 'view this end of day price task');

function QuoteMediaEodPriceTaskDetail() {
  const { taskId } = Route.useParams();
  const request = { taskId } as const;
  const { data, error, isPending } = useExcQuotemediaV1EodPriceTaskQueryServiceEodPriceTaskGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.eodPriceTask), [data?.eodPriceTask]);
  const menuItems = useMemo(() => getMenuItems(taskId), [taskId]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <QuotemediaEodPriceTaskStatusPill isPending={showPending} size="large" status={data?.eodPriceTask?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <QuoteMediaEodPriceTaskEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <WrappingLayoutContainer
          gap={DEFAULT_PAGE_LAYOUT_GAP}
          left={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
          }}
          padding={LAYOUT_ZERO_CONTAINER_PADDING}
          right={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
          }}
          wrap="forward"
          wrapGap={7}
        />
      </PageLayout.Section>
    </PageLayout>
  );
}
